/**
 * QueueEditDialog Component
 * Dialog for editing queue number manually
 *
 * @version 2025.1.0
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { ReceptionAppointment } from '../../../types/reception';

interface QueueEditDialogProps {
  open: boolean;
  appointment: ReceptionAppointment | null;
  loading?: boolean;
  onConfirm: (newQueueNumber: number) => void;
  onClose: () => void;
}

export const QueueEditDialog: React.FC<QueueEditDialogProps> = ({
  open,
  appointment,
  loading = false,
  onConfirm,
  onClose,
}) => {
  const [queueNumber, setQueueNumber] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Initialize state when dialog opens via transition callback
  const handleEnter = useCallback(() => {
    if (appointment) {
      setQueueNumber(appointment.queueNumber?.toString() || '');
      setError('');
    }
  }, [appointment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setQueueNumber(value);
      setError('');
    }
  };

  const handleConfirm = () => {
    const num = parseInt(queueNumber, 10);
    if (isNaN(num) || num < 1) {
      setError('Informe um número válido maior que zero');
      return;
    }
    onConfirm(num);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleConfirm();
    }
  };

  if (!appointment) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>Editar Senha</DialogTitle>
      <DialogContent>
        <Box py={1}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Funcionário: <strong>{appointment.employeeName}</strong>
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Número da Senha"
            value={queueNumber}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={!!error}
            helperText={error || 'Informe o novo número da senha'}
            disabled={loading}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading || !queueNumber}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QueueEditDialog;
